import React from 'react';
import { X, WifiOff, Globe, BookOpen } from 'lucide-react';

interface OfflineModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureName?: string;
}

export const OfflineModal: React.FC<OfflineModalProps> = ({ 
  isOpen, 
  onClose, 
  featureName = "This feature" 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-md transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal Dialog Card */}
      <div className="relative w-full max-w-sm bg-white rounded-[2.5rem] shadow-[0_30px_60px_rgba(0,0,0,0.3)] border border-slate-100 p-8 text-center z-10 animate-in fade-in zoom-in-95 duration-200">
        <button 
          onClick={onClose}
          className="absolute top-5 right-5 p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all active:scale-95 cursor-pointer"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="bg-amber-50 p-5 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center border border-amber-100">
          <WifiOff className="w-10 h-10 text-amber-600 animate-pulse" />
        </div>

        <h3 className="font-cinzel font-bold text-xl text-slate-900 mb-2">
          Connection Required
        </h3>
        
        <p className="text-xs text-slate-500 font-medium leading-relaxed mb-6 font-sans">
          <strong>{featureName}</strong> is currently unavailable because you are offline. Please connect to the internet to perform external interactions.
        </p>

        <div className="bg-blue-50/50 rounded-2xl p-4 mb-6 border border-blue-100/50 text-left">
          <span className="text-[10px] font-bold text-blue-800 uppercase tracking-wider block mb-1 flex items-center gap-1">
            <BookOpen className="w-3.5 h-3.5 text-blue-600" />
            Playable Offline:
          </span>
          <p className="text-[11px] text-blue-900 leading-normal font-medium">
            You can still enjoy full local gameplay, read Bible stories, view unlocked visual verse art, and track your metrics locally without any signal.
          </p>
        </div>

        <button
          onClick={onClose}
          className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl text-xs font-bold transition active:scale-95 cursor-pointer font-sans shadow-md"
        >
          Resume Playing Offline
        </button>
      </div>
    </div>
  );
};
